/**
 * Sending the queue — docs/sync-state-machine.md §3, §10.
 *
 * Separate from the queue so the retry rules can be tested without IndexedDB
 * and the storage rules without a network.
 *
 * The response table from §10, and why each row is what it is:
 *
 *   network / 408 / 429 / 5xx  →  pending, backoff. The court has bad signal;
 *                                 this is the expected condition, not an error.
 *   401 / 403                  →  pending, prompt to sign in. Rule R-1: tokens
 *                                 expire, queues do not. Nothing is lost.
 *   409 (conflict)             →  conflict. A human decides; nothing was
 *                                 applied server-side.
 *   400 / 422                  →  failed. Needs correction, not repetition.
 */

import type { OpState, SyncOp, SyncQueue } from "./queue.js";

export interface OpResult {
  clientUuid: string;
  status: "applied" | "replayed" | "conflict" | "rejected" | "held";
  entityId?: string;
  version?: number;
  serverVersion?: number;
  serverDeleted?: boolean;
  conflictId?: string;
  message?: string;
  quarantined?: boolean;
}

export interface SyncResponse {
  results: OpResult[];
  stoppedAt: string | null;
  clockSkewSeconds?: number;
}

export interface FlushOutcome {
  sent: number;
  applied: number;
  conflicts: number;
  failed: number;
  /** Set when the batch could not be delivered at all. */
  offline?: boolean;
  /** Set when the server refused the session; the queue is untouched. */
  needsAuth?: boolean;
  clockSkewSeconds?: number;
}

/** §10 — min(2^attempts x 1s, 5 min), with ±20% jitter. */
export function backoffMs(attempts: number, random: () => number = Math.random): number {
  const base = Math.min(2 ** attempts * 1000, 5 * 60 * 1000);
  const jitter = 1 + (random() * 0.4 - 0.2);
  return Math.round(base * jitter);
}

/** §10 — after ten consecutive failures, offer the export escape hatch. */
export const FAILURE_BANNER_THRESHOLD = 10;

/** ST-2 — a queue this deep or this old gets a persistent warning. */
export const QUEUE_WARNING_DEPTH = 50;
export const QUEUE_WARNING_AGE_MS = 24 * 60 * 60 * 1000;

export type Transport = (body: {
  deviceId: string;
  ops: SyncOp[];
}) => Promise<{ status: number; body?: SyncResponse; retryAfterSeconds?: number }>;

/** The real transport. Kept tiny so the sender's logic is what gets tested. */
export const httpTransport: Transport = async (body) => {
  const response = await fetch("/api/sync", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const retryAfter = Number(response.headers.get("retry-after"));
  let parsed: SyncResponse | undefined;
  try {
    parsed = (await response.json()) as SyncResponse;
  } catch {
    parsed = undefined;
  }

  return {
    status: response.status,
    body: parsed,
    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
  };
};

export class Sender {
  constructor(
    private readonly queue: SyncQueue,
    private readonly transport: Transport = httpTransport,
    private readonly options: { now?: () => number; random?: () => number } = {},
  ) {}

  private now(): number {
    return this.options.now?.() ?? Date.now();
  }

  /**
   * Sends one batch. Collapses first, so churn a member made and undid before
   * the connection returned is never transmitted at all (§4).
   */
  async flush(): Promise<FlushOutcome> {
    await this.queue.collapse();

    const batch = await this.queue.due(this.now());
    if (batch.length === 0) {
      return { sent: 0, applied: 0, conflicts: 0, failed: 0 };
    }

    await this.queue.markInflight(batch.map((op) => op.clientUuid));

    let response: Awaited<ReturnType<Transport>>;
    try {
      response = await this.transport({ deviceId: batch[0].deviceId, ops: batch });
    } catch {
      // Unreachable. Expected at a sports hall; not an error state.
      await this.retryLater(batch);
      return { sent: batch.length, applied: 0, conflicts: 0, failed: 0, offline: true };
    }

    if (response.status === 401 || response.status === 403) {
      // R-1 — tokens expire, queues do not.
      await this.returnToPending(batch, "Sign in again to sync.");
      return { sent: batch.length, applied: 0, conflicts: 0, failed: 0, needsAuth: true };
    }

    if (response.status === 429 || response.status >= 500 || response.status === 408) {
      await this.retryLater(batch, response.retryAfterSeconds);
      return { sent: batch.length, applied: 0, conflicts: 0, failed: 0, offline: true };
    }

    if (!response.body || response.status >= 400) {
      // A 400 on the batch itself, not on one op: the request shape is wrong,
      // which repetition will not fix.
      await this.markAll(batch, "failed", "The app sent something the server could not read.");
      return { sent: batch.length, applied: 0, conflicts: 0, failed: batch.length };
    }

    return this.applyResults(batch, response.body);
  }

  private async applyResults(batch: SyncOp[], body: SyncResponse): Promise<FlushOutcome> {
    let applied = 0;
    let conflicts = 0;
    let failed = 0;
    const seen = new Set<string>();

    for (const result of body.results) {
      seen.add(result.clientUuid);

      switch (result.status) {
        case "applied":
        case "replayed":
          applied += 1;
          await this.queue.update(result.clientUuid, {
            state: "synced",
            error: null,
            entityId: result.entityId ?? null,
          });
          break;

        case "conflict":
          conflicts += 1;
          await this.queue.update(result.clientUuid, {
            state: "conflict",
            error: `Someone else changed this. Their version is ${String(result.serverVersion)}.`,
          });
          break;

        case "rejected":
          failed += 1;
          await this.queue.update(result.clientUuid, {
            state: "failed",
            error: result.message ?? "The server refused this.",
          });
          break;

        case "held":
          // Not tried. Back to pending, ready for the next batch, with no
          // attempt counted against it — it did not fail.
          await this.queue.update(result.clientUuid, { state: "pending" });
          break;
      }
    }

    // Anything the server did not mention goes back to pending rather than
    // being assumed sent. I-3: no op is ever discarded by the system.
    for (const op of batch) {
      if (!seen.has(op.clientUuid)) {
        await this.queue.update(op.clientUuid, { state: "pending" });
      }
    }

    return {
      sent: batch.length,
      applied,
      conflicts,
      failed,
      ...(body.clockSkewSeconds ? { clockSkewSeconds: body.clockSkewSeconds } : {}),
    };
  }

  private async retryLater(batch: SyncOp[], retryAfterSeconds?: number): Promise<void> {
    for (const op of batch) {
      const attempts = op.attempts + 1;
      const delay =
        retryAfterSeconds !== undefined
          ? retryAfterSeconds * 1000
          : backoffMs(attempts, this.options.random);
      await this.queue.update(op.clientUuid, {
        state: "pending",
        attempts,
        nextAttemptAt: this.now() + delay,
      });
    }
  }

  private async returnToPending(batch: SyncOp[], error: string): Promise<void> {
    for (const op of batch) {
      // Deliberately no attempts++ and no backoff: this is not the op's fault
      // and it should go the moment the member signs in again.
      await this.queue.update(op.clientUuid, { state: "pending", error, nextAttemptAt: 0 });
    }
  }

  private async markAll(batch: SyncOp[], state: OpState, error: string): Promise<void> {
    for (const op of batch) {
      await this.queue.update(op.clientUuid, { state, error });
    }
  }

  /**
   * Retry immediately on regained connectivity, regardless of next_attempt_at
   * (§10). Backoff protects a struggling server; it should not make a member
   * wait five minutes after their signal came back.
   */
  async onReconnect(): Promise<FlushOutcome> {
    const pending = await this.queue.byState("pending");
    for (const op of pending) {
      await this.queue.update(op.clientUuid, { nextAttemptAt: 0 });
    }
    return this.flush();
  }
}
